import type { ApiResponse, ListWithTotal } from "@entrilia/shared/api/types";
import type { ObjectFieldValues, SearchObjectsRequest } from "../../../../../../api/types/objectTypes";
import type { ObjectSelectConfiguration } from "../../../../../../api/types/workflowTypes";

export interface ObjectSelectOptions {
  [fieldName: string]: ObjectFieldValues[];
}

interface ObjectSelectSearchRequest {
  fieldName: string;
  objectType: string;
  payload: SearchObjectsRequest;
}

type SearchObjects = (
  objectType: string,
  payload: SearchObjectsRequest
) => Promise<ApiResponse<ListWithTotal<ObjectFieldValues>>>;

export const buildObjectSelectSearchRequests = (
  objectSelectConfigurations: ObjectSelectConfiguration[]
): ObjectSelectSearchRequest[] =>
  objectSelectConfigurations.map((configuration) => ({
    fieldName: configuration.fieldName,
    objectType: configuration.objectType,
    payload: {
      fieldIds: [
        configuration.objectFieldName,
        ...configuration.additionalFields.map((additionalField) => additionalField.objectFieldName),
      ],
      filters: configuration.filters,
    },
  }));

export const loadObjectSelectOptions = async (
  objectSelectConfigurations: ObjectSelectConfiguration[],
  searchObjects: SearchObjects
): Promise<ApiResponse<ObjectSelectOptions>> => {
  const requests = buildObjectSelectSearchRequests(objectSelectConfigurations).map(async (request) => ({
    fieldName: request.fieldName,
    response: await searchObjects(request.objectType, request.payload),
  }));

  const responses = await Promise.all(requests);
  const data: ObjectSelectOptions = {};

  for (const { fieldName, response } of responses) {
    if (!response.success) {
      return { success: false, error: response.error };
    }

    data[fieldName] = response.data.items;
  }

  return { success: true, data };
};

export const getObjectSelectValue = (
  fieldValue: unknown,
  configuration: ObjectSelectConfiguration,
  objectSelectOptions: ObjectSelectOptions
): ObjectFieldValues | null => {
  const options = objectSelectOptions[configuration.fieldName] ?? [];
  return options.find((option) => option.fieldValues[configuration.objectFieldName] === fieldValue) ?? null;
};

export const buildObjectFieldUpdates = (
  configuration: ObjectSelectConfiguration,
  selectedObject: ObjectFieldValues | null
): Record<string, unknown> => {
  const fieldUpdates: Record<string, unknown> = {
    [configuration.fieldName]: selectedObject?.fieldValues[configuration.objectFieldName] ?? null,
  };

  for (const additionalField of configuration.additionalFields) {
    fieldUpdates[additionalField.changeSetFieldName] =
      selectedObject?.fieldValues[additionalField.objectFieldName] ?? null;
  }

  return fieldUpdates;
};
